/**
 * FIREBASE'S HEARTBEAT NEVER HOLDS A REQUEST (2026-10-31, the sign-in stalls).
 *
 * Every request Firebase Auth makes first awaits the app's heartbeat header (`_getAdditionalHeaders` in
 * node_modules/@firebase/auth). That includes the boot lookup its first answer waits for. The heartbeat service's
 * first read comes from its own browser database (`firebase-heartbeat-database`), so when that database is slow or
 * held, sign-in waits for it; the web-vitals record named it once in a week. The header is the SDK's usage report to
 * Google, and nothing of ours reads it. A request without it works the same.
 *
 * So the service's `getHeartbeatsHeader` answers within HEARTBEAT_HEADER_WAIT_MS: whatever the service has by then, or
 * nothing. A read that finishes later still marks that day's heartbeat as sent, as the SDK does after a send, so at
 * worst one day's report is not made. Applied once per service; anything without the expected method is left alone.
 * A leaf with no imports: utils/firebase.ts hands in the service, and the Chromium probe
 * (scripts/probe-auth-held-database.mjs) runs this same file against the real SDK.
 */

/** How long a request may wait for the heartbeat header. Its database answers in a few ms when it is free. */
export const HEARTBEAT_HEADER_WAIT_MS = 200;

const BOUND = '__prest0HeartbeatHeaderBound';

type HeartbeatService = { getHeartbeatsHeader: () => Promise<string> } & Record<string, unknown>;

/** What happened: the method now answers in time, it already did, or there was no such service to change. */
export type HeartbeatBoundOutcome = 'bounded' | 'already' | 'absent';

export function boundHeartbeatHeader(
  service: unknown,
  waitMs: number = HEARTBEAT_HEADER_WAIT_MS,
  setTimer: (fn: () => void, ms: number) => unknown = (fn, ms) => setTimeout(fn, ms),
): HeartbeatBoundOutcome {
  const target = service as HeartbeatService | null | undefined;
  if (!target || typeof target.getHeartbeatsHeader !== 'function') return 'absent';
  if (target[BOUND] === true) return 'already';
  const read = target.getHeartbeatsHeader.bind(target);
  target.getHeartbeatsHeader = () => new Promise<string>((resolve) => {
    let settled = false;
    const settle = (header: string) => {
      if (settled) return;
      settled = true;
      resolve(header);
    };
    setTimer(() => settle(''), waitMs);
    let pending: Promise<string>;
    try {
      pending = read();
    } catch {
      settle('');
      return;
    }
    Promise.resolve(pending).then((header) => settle(typeof header === 'string' ? header : ''), () => settle(''));
  });
  target[BOUND] = true;
  return 'bounded';
}
